import Header from './components/Header'
import Hero from './components/Hero'
import Footer from './components/Footer'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

// Site configuration for worlddayforglaciers.com
const SITE_CONFIG = {
  name: 'World Day for Glaciers',
  description: 'Education & Public Awareness',
  domain: 'worlddayforglaciers.com',
  
  hero: {
    title: 'Educate, Inspire, Protect Glaciers',
    subtitle: 'Public Awareness & Conservation Education',
    description: 'Discover educational resources, awareness campaigns, and practical guides to protect glaciers. Learn how individuals and communities can make a difference for our planet\'s frozen heritage.',
    primaryButtonText: 'Educational Resources',
    primaryButtonLink: '/education',
    secondaryButtonText: 'Take Action',
    secondaryButtonLink: '/action',
  },
  
  relatedSites: [
    {
      name: 'Glaciers Day',
      description: 'Official glacier day celebrations and events',
      domain: 'glaciersday.com',
    },
    {
      name: 'World Glaciers Data',
      description: 'Scientific data and glacier monitoring',
      domain: 'worldglaciersday.com',
    },
  ],
}

function App() {
  return (
    <>
      <SEO 
        title="World Day for Glaciers - Global Education & Awareness"
        description="Educational platform for glacier conservation awareness, climate education, and public engagement. Join the global movement to protect glaciers."
        keywords="World Day for Glaciers, glacier conservation, climate action, environmental protection, polar regions, ice, sustainability,冰川保护日,冰川,气候行动,环境保护,极地,冰,可持续性"
        type="website"
      />
      <div className="min-h-screen flex flex-col">
      <Header 
        siteName={SITE_CONFIG.name}
        siteDescription={SITE_CONFIG.description}
      />
      
      <main className="flex-grow">
        <Hero {...SITE_CONFIG.hero} />
        
        <ShopSection />
        <NewsSection 
          website="worlddayforglaciers"
          limit={10000}
          title="Glacier Education News"
          themeColor="emerald"
        />
        {/* Education Focus Section */}
        <section className="section-padding bg-glacier-50">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-4">
                Educational Resources & Awareness
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Access free educational materials, awareness campaigns, and practical guides to learn about glacier conservation and climate action.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">📚</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Learning Materials</h3>
                <p className="text-gray-600 mb-4">
                  Free lesson plans, educational videos, and interactive activities for schools, universities, and community groups.
                </p>
                <a href="/learning" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  Access materials →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Awareness Campaigns</h3>
                <p className="text-gray-600 mb-4">
                  Join global awareness campaigns, social media initiatives, and community outreach programs to spread the word.
                </p>
                <a href="/campaigns" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  Join campaigns →
                </a>
              </div>
              
              <div className="bg-white p-8 rounded-xl shadow-lg border border-glacier-100">
                <div className="w-12 h-12 bg-glacier-100 rounded-lg flex items-center justify-center mb-6">
                  <span className="text-2xl">👥</span>
                </div>
                <h3 className="text-xl font-bold text-glacier-900 mb-4">Community Action</h3>
                <p className="text-gray-600 mb-4">
                  Practical guides for community-led conservation projects, volunteer opportunities, and local advocacy initiatives.
                </p>
                <a href="/community" className="text-glacier-600 hover:text-glacier-800 font-medium">
                  Get involved →
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {/* Action Guide Section */}
        <section className="section-padding">
          <div className="container-padding mx-auto max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-glacier-900 mb-6">
                  Take Action for Glaciers
                </h2>
                <p className="text-gray-600 mb-6">
                  Everyone can contribute to glacier conservation. Our platform provides practical steps for individuals, schools, businesses, and communities to make a real difference in protecting our planet's frozen heritage.
                </p>
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">1</span>
                    </div>
                    <span className="text-gray-700">Reduce your carbon footprint with our carbon calculator</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">2</span>
                    </div>
                    <span className="text-gray-700">Join local conservation groups and volunteer programs</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">3</span>
                    </div>
                    <span className="text-gray-700">Advocate for climate policies that protect glaciers</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-glacier-100 rounded-lg flex items-center justify-center">
                      <span className="text-glacier-600">4</span>
                    </div>
                    <span className="text-gray-700">Support sustainable tourism in glacier regions</span>
                  </div>
                </div>
                <a href="/action-guide" className="btn-primary">
                  Download Action Guide
                </a>
              </div>
              <div className="bg-gradient-to-br from-glacier-100 to-glacier-200 rounded-2xl p-8">
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🏫</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">5,000+ Schools</h4>
                      <p className="text-gray-600 text-sm">Using our educational materials</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">🌍</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">150 Countries</h4>
                      <p className="text-gray-600 text-sm">Reached by our campaigns</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                      <span className="text-2xl">📱</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-glacier-900">2M+ Followers</h4>
                      <p className="text-gray-600 text-sm">Across social media platforms</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Education Contact Section - Prominent Display */}
        <section className="section-padding bg-gradient-to-r from-glacier-500 to-glacier-700 text-white">
          <div className="container-padding mx-auto max-w-7xl text-center">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Educational & Community Inquiries
              </h2>
              <p className="text-xl text-glacier-200 mb-8">
                For educational resources, community programs, awareness campaigns, and partnership opportunities.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20">
                <div className="flex flex-col md:flex-row items-center justify-center gap-6">
                  <div className="flex items-center justify-center w-16 h-16 bg-white rounded-full">
                    <span className="text-3xl">📚</span>
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-bold mb-2">Education & Community Contact</h3>
                    <a 
                      href="mailto:info@worlddayforglaciers.com" 
                      className="text-2xl md:text-3xl font-bold text-white hover:text-glacier-200 transition-colors inline-block"
                    >
                      info@worlddayforglaciers.com
                    </a>
                    <p className="text-glacier-200 mt-3">
                      For educational materials, community programs, awareness campaigns, and partnership inquiries
                    </p>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-bold mb-2">For Educators</h4>
                      <p className="text-glacier-200 text-sm">Lesson plans, classroom resources, school partnerships</p>
                    </div>
                    <div>
                      <h4 className="font-bold mb-2">For Communities</h4>
                      <p className="text-glacier-200 text-sm">Local campaigns, volunteer programs, awareness events</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
      </main>
      
      <Footer 
        currentSite={{
          name: SITE_CONFIG.name,
          description: SITE_CONFIG.description,
          domain: SITE_CONFIG.domain,
        }}
        relatedSites={SITE_CONFIG.relatedSites}
      />
    </div>
    </>
  )
}

export default App